import { resolveMediaUrl } from '@/utils/mediaUrl'

const API_PREFIXES = [
  '/dev-api/portal/open/content',
  '/prod-api/portal/open/content'
]

function trimQuotedText (value) {
  if (typeof value !== 'string') {
    return ''
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1).trim()
  }
  return trimmed
}

function getUploadOrigin () {
  if (typeof window === 'undefined' || !window.location || !window.location.origin) {
    return ''
  }
  return window.location.origin.replace(/\/$/, '')
}

function uniqueList (items) {
  return items.filter((item, index) => item && items.indexOf(item) === index)
}

function buildGatewayFileUrl (path, prefix) {
  const uploadOrigin = getUploadOrigin()
  if (!path) {
    return ''
  }
  return uploadOrigin ? `${uploadOrigin}${prefix}${path}` : `${prefix}${path}`
}

function buildProfileUrlCandidates (path) {
  if (!path) {
    return []
  }

  const directUrl = buildGatewayFileUrl(path, '')
  const prodApiUrl = buildGatewayFileUrl(path, '/prod-api')

  return uniqueList([
    directUrl,
    prodApiUrl
  ])
}

function canProbeWithFetch (url) {
  if (typeof window === 'undefined' || typeof window.fetch === 'undefined' || !url) {
    return false
  }

  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch (e) {
    return false
  }
}

function preloadImage (url, timeoutMs) {
  if (canProbeWithFetch(url)) {
    const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null
    let timer = null

    if (controller && timeoutMs > 0) {
      timer = window.setTimeout(() => controller.abort(), timeoutMs)
    }

    return window.fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller ? controller.signal : undefined
    })
      .then(response => {
        if (timer) {
          window.clearTimeout(timer)
        }
        return response.ok
      })
      .catch(() => {
        if (timer) {
          window.clearTimeout(timer)
        }
        return false
      })
  }

  return new Promise(resolve => {
    if (typeof window === 'undefined' || typeof window.Image === 'undefined' || !url) {
      resolve(false)
      return
    }

    const image = new window.Image()
    let settled = false
    let timer = null

    const finish = status => {
      if (settled) {
        return
      }
      settled = true
      image.onload = null
      image.onerror = null
      if (timer) {
        window.clearTimeout(timer)
      }
      resolve(status)
    }

    image.onload = () => finish(true)
    image.onerror = () => finish(false)

    if (timeoutMs > 0) {
      timer = window.setTimeout(() => finish(false), timeoutMs)
    }

    image.src = url
    if (image.complete) {
      finish(true)
    }
  })
}

async function requestJson (path) {
  let lastError = null
  for (const prefix of API_PREFIXES) {
    try {
      const response = await fetch(`${prefix}${path}`)
      const result = await response.json()
      if (result.code !== 200) {
        throw new Error(result.msg || '请求失败')
      }
      return result.data
    } catch (error) {
      lastError = error
    }
  }
  throw lastError || new Error('请求失败')
}

export function fetchPortalHomeContent (limit = 3) {
  return requestJson(`/home?limit=${limit}`)
}

export function fetchPortalContentList ({ category, pageNum = 1, pageSize = 10 } = {}) {
  const params = new URLSearchParams({
    category: category || '',
    pageNum: String(pageNum),
    pageSize: String(pageSize)
  })
  return requestJson(`/list?${params.toString()}`)
}

export function fetchPortalContentDetail (contentId) {
  return requestJson(`/${contentId}`)
}

export function resolvePortalFileUrl (url) {
  const raw = trimQuotedText(url)
  if (!raw) {
    return ''
  }

  if (
    raw.indexOf('/profile/') !== -1 ||
    raw.indexOf('/miniprogram-profile/') !== -1 ||
    raw.startsWith('profile/') ||
    raw.startsWith('miniprogram-profile/')
  ) {
    return resolveMediaUrl(raw)
  }

  if (/^https?:\/\//.test(raw)) {
    return raw
  }

  return raw.startsWith('/') ? raw : `/${raw}`
}

export function resolvePortalFileUrlCandidates (url) {
  const raw = trimQuotedText(url)
  if (!raw) {
    return []
  }

  const profileIndex = raw.indexOf('/profile/')
  if (profileIndex !== -1) {
    const finalPath = raw.substring(profileIndex)
    return buildProfileUrlCandidates(finalPath)
  }

  const miniProfileIndex = raw.indexOf('/miniprogram-profile/')
  if (miniProfileIndex !== -1) {
    const finalPath = `/profile${raw.substring(miniProfileIndex + '/miniprogram-profile'.length)}`
    return buildProfileUrlCandidates(finalPath)
  }

  if (/^https?:\/\//.test(raw)) {
    return [raw]
  }

  return [raw.startsWith('/') ? raw : `/${raw}`]
}

export async function preloadPortalImage (urlCandidates, { timeoutMs = 8000 } = {}) {
  const candidates = uniqueList((Array.isArray(urlCandidates) ? urlCandidates : [])
    .map(item => trimQuotedText(item))
    .filter(Boolean))

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index]
    const loaded = await preloadImage(candidate, timeoutMs)
    if (loaded) {
      return {
        url: candidate,
        index
      }
    }
  }

  return null
}

export function splitAttachmentUrls (attachmentUrls) {
  if (!attachmentUrls) {
    return []
  }
  return attachmentUrls
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => ({
      name: item.split('/').pop(),
      url: resolvePortalFileUrl(item)
    }))
}

export function formatPortalDate (dateString) {
  if (!dateString) {
    return ''
  }
  const date = new Date(dateString.replace(/-/g, '/'))
  if (Number.isNaN(date.getTime())) {
    return dateString
  }
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}



// WEBPACK FOOTER //
// ./src/utils/content.js